import axios from "../../interceptors/axiosInterceptor";
import {
  GET_PRODUCT_TYPES_URL,
  GET_UNIT_OF_MEASUREMENT_URL,
  FETCH_PRODUCT_FOR_ORDERS_URL,
  GET_PRODUCT_BRANDS_URL,
  GET_WAREHOUSE_URL,
  GET_ODOO_TAX_LIST,
  FETCH_CITIES,
  CREATE_GLOBAL_PRODUCT_URL,
  UPDATE_GLOBAL_PRODUCT_URL,
  GET_GLOBAL_PRODUCT_DETAILS_URL,
  GET_DELIVERY_OPTIONS_URL,
} from "../../config/Url";
import {
  IProductCategory,
  IGetQueryParams,
  IWarehousePayload,
  IOdooTaxResponse,
  IOdooTaxListResponse,
  IGetCityParams,
  IIdValuePairResponse,
  IGlobalProductPayload,
  IFetchProductsForOrderPayload,
  ICheckProductResponsePayload,
  IDeliveryOption,
} from "../../interfaces/Index";
import { getAuthOptions } from "../../config/Headers";
import { normalizeCatchErrors } from "../../utils/errorUtils";
import {
  normalizeFetchProductResponse,
  normalizeProductType,
  normalizeProductUom,
  normalizeProductBrand,
  normalizeWareHouseNames,
  normalizeOdooTax,
  normalizeIdValueResponse,
  normalizeGlobalProductDetailsResponse,
  normalizeDeliveryOptions,
} from "../../normalizer/GlobalProductNormalizer";

export const createProductGlobalApiCall = (payload: FormData) =>
  new Promise<void>((resolve, reject) => {
    axios
      .post(CREATE_GLOBAL_PRODUCT_URL, payload, getAuthOptions(true))
      .then((response) => {
        if (response) {
          resolve();
        } else {
          reject({ errors: "Something went wrong" });
        }
      })
      .catch((error) => {
        const errorPayload = normalizeCatchErrors(error.response);
        reject(errorPayload);
      });
  });

export const fetchProductTypeApiCall = () =>
  new Promise<Array<IProductCategory>>((resolve, reject) => {
    axios
      .get(GET_PRODUCT_TYPES_URL, getAuthOptions())
      .then((response) => {
        if (response) {
          const data = response && response.data;
          resolve(normalizeProductType(data));
        } else {
          reject({ errors: "Something went wrong" });
        }
      })
      .catch((error) => {
        const errorPayload = normalizeCatchErrors(error.response);
        reject(errorPayload);
      });
  });

export const fetchProductBrandApiCall = (queryParam: IGetQueryParams = {}) =>
  new Promise<Array<IProductCategory>>((resolve, reject) => {
    let requestConfig: any = getAuthOptions();
    requestConfig.params = queryParam;
    axios
      .get(GET_PRODUCT_BRANDS_URL, requestConfig)
      .then((response) => {
        if (response) {
          const data = response && response.data;
          resolve(normalizeProductBrand(data));
        } else {
          reject({ errors: "Something went wrong" });
        }
      })
      .catch((error) => {
        const errorPayload = normalizeCatchErrors(error.response);
        reject(errorPayload);
      });
  });

export const fetchProductUomApiCall = (id: string = "") =>
  new Promise<Array<IProductCategory>>((resolve, reject) => {
    let url = GET_UNIT_OF_MEASUREMENT_URL;
    if (id) {
      url = `${GET_UNIT_OF_MEASUREMENT_URL}?uomId=${id}`;
    }
    axios
      .get(url, getAuthOptions())
      .then((response) => {
        if (response) {
          const data = response && response.data;
          resolve(normalizeProductUom(data));
        } else {
          reject({ errors: "Something went wrong" });
        }
      })
      .catch((error) => {
        const errorPayload = normalizeCatchErrors(error.response);
        reject(errorPayload);
      });
  });

export const fetchProductsForOrderApiCall = (
  payload: IFetchProductsForOrderPayload
) =>
  new Promise<ICheckProductResponsePayload[]>((resolve, reject) => {
    axios
      .post(FETCH_PRODUCT_FOR_ORDERS_URL, payload, getAuthOptions())
      .then((response) => {
        if (!response) reject({ errors: "Failed to fetch Products for Order" });

        const data = response && response.data;
        const pageNumber: number = payload.paginationDetails.page
          ? Number(payload.paginationDetails.page) + 1
          : 1;
        const isPaginatedRes =
          payload.paginationDetails.page &&
          (payload.paginationDetails.page as number) > 0 &&
          data.length >= 50
            ? true
            : false;
        resolve(
          normalizeFetchProductResponse(data, pageNumber, isPaginatedRes)
        );
      })
      .catch((error) => reject(normalizeCatchErrors(error.response)));
  });

export const multiPartUploadApiCall = (
  payload: FormData,
  url: string,
  legacy: boolean = true
) =>
  new Promise<void>((resolve, reject) => {
    const axiosVerb = legacy ? axios.post : axios.put;
    axiosVerb(url, payload, getAuthOptions(true))
      .then((response) => {
        if (response) {
          resolve();
        } else {
          reject({ errors: "Something went wrong" });
        }
      })
      .catch((error) => {
        const errorPayload = normalizeCatchErrors(error.response);
        reject(errorPayload);
      });
  });

export const fetchGlobalProductsDetailsApiCall = (id: string) =>
  new Promise<IGlobalProductPayload>((resolve, reject) => {
    let url = GET_GLOBAL_PRODUCT_DETAILS_URL.replace("{product_id}", id);
    axios
      .get(url, getAuthOptions())
      .then((response) => {
        if (response) {
          const data = response.data;
          const normalizedRes: any =
            normalizeGlobalProductDetailsResponse(data);
          resolve(normalizedRes);
        } else {
          reject({ errors: "Something went wrong" });
        }
      })
      .catch((error) => {
        const errorPayload = normalizeCatchErrors(error.response);
        reject(errorPayload);
      });
  });

export const updateGlobalProductApiCall = (id: string, payload: FormData) =>
  new Promise<void>((resolve, reject) => {
    let url = UPDATE_GLOBAL_PRODUCT_URL.replace("{product_id}", id as string);
    axios
      .put(url, payload, getAuthOptions(true))
      .then((response) => {
        if (response) {
          resolve();
        } else {
          reject({ errors: "Something went wrong" });
        }
      })
      .catch((error) => {
        const errorPayload = normalizeCatchErrors(error.response);
        reject(errorPayload);
      });
  });

export const fetchWareHouseNamesApiCall = () =>
  new Promise<IWarehousePayload[]>((resolve, reject) => {
    axios
      .get(GET_WAREHOUSE_URL, getAuthOptions())
      .then((response) => {
        if (response) {
          const data = response && response.data;
          resolve(normalizeWareHouseNames(data));
        } else {
          reject({ errors: "Something went wrong" });
        }
      })
      .catch((error) => {
        const errorPayload = normalizeCatchErrors(error.response);
        reject(errorPayload);
      });
  });

export const fetchDeliveryOptionsApiCall = () =>
  new Promise<IDeliveryOption[]>((resolve, reject) => {
    axios
      .get(GET_DELIVERY_OPTIONS_URL, getAuthOptions())
      .then((response) => {
        if (response) {
          const data = response && response.data;
          resolve(normalizeDeliveryOptions(data));
        } else {
          reject({ errors: "Something went wrong" });
        }
      })
      .catch((error) => {
        const errorPayload = normalizeCatchErrors(error.response);
        reject(errorPayload);
      });
  });

export const fetchOdooTaxApiCall = (id: string = "") =>
  new Promise<IOdooTaxListResponse[] | IOdooTaxResponse>((resolve, reject) => {
    let url = GET_ODOO_TAX_LIST.replace("{tax_id}", id);
    axios
      .get(url, getAuthOptions())
      .then((response) => {
        if (response) {
          const data: IOdooTaxListResponse[] | IOdooTaxResponse = response.data;
          resolve(normalizeOdooTax(data));
        } else {
          reject({ errors: "Something went wrong" });
        }
      })
      .catch((error) => {
        const errorPayload = normalizeCatchErrors(error.response);
        reject(errorPayload);
      });
  });

export const fetchCitiesApiCall = (queryParam: IGetCityParams = {}) =>
  new Promise<Array<IIdValuePairResponse>>((resolve, reject) => {
    let requestConfig: any = getAuthOptions();
    requestConfig.params = queryParam;
    axios
      .get(FETCH_CITIES, requestConfig)
      .then((response) => {
        if (response) {
          const data = response.data;
          resolve(normalizeIdValueResponse(data));
        } else {
          reject({ errors: "Something went wrong" });
        }
      })
      .catch((error) => {
        const errorPayload = normalizeCatchErrors(error.response);
        reject(errorPayload);
      });
  });
